import { useRef } from 'react';
import { Icon } from 'semantic-ui-react';
import toast from 'react-hot-toast';

import AsyncRequestButton from './AsyncRequestButton';
import { usePermissions } from '~/requests/permissions/usePermissions';
import {
  startCimGeneration,
  startCimGenerationForProject,
  pollCimGeneration,
} from '~/api/cimGeneration';
import { ApiEvent } from '~/types/ApiEvent';

interface Props {
  dealType: 'portfolio' | 'project';
  dealId: string;
}

export default function GenerateCimButton({ dealType, dealId }: Props) {
  const { permissions } = usePermissions();
  const latestSlidesUrl = useRef<string | null>(null);

  if (!permissions.isAdmin || !dealId) return null;

  return (
    <AsyncRequestButton
      text={<><Icon name='file powerpoint outline' />Generate CIM</>}
      loadingText={<><Icon name='file powerpoint outline' />Generating CIM</>}
      onClick={async (): Promise<ApiEvent> => {
        latestSlidesUrl.current = null;
        const { apiEvent } = dealType === 'portfolio'
          ? await startCimGeneration(dealId)
          : await startCimGenerationForProject(dealId);
        return {
          id: apiEvent.id,
          status: 'PENDING',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
      }}
      pollFn={async (apiEventId): Promise<ApiEvent> => {
        const r = await pollCimGeneration(apiEventId);
        if (r.status === 'ERROR') {
          const msg = r.result?.message ?? 'CIM generation failed.';
          const details = r.result?.details;
          throw new Error(details ? `${msg} (${details})` : msg);
        }
        if (r.status === 'SUCCESS' && r.result?.slidesUrl) {
          latestSlidesUrl.current = r.result.slidesUrl;
        }
        return {
          id: apiEventId,
          status: r.status,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          result: r.result,
        };
      }}
      onSuccess={() => {
        const url = latestSlidesUrl.current;
        if (url) {
          window.open(url, '_blank', 'noopener,noreferrer');
          toast.success('CIM generated. Opened in new tab.');
        } else {
          toast.error('CIM generated but no slides URL was returned.');
        }
      }}
      onError={(err: any) => {
        const msg = err?.message ?? err?.response?.data?.message ?? 'Failed to generate CIM.';
        toast.error(msg);
      }}
      onTimeout={() => toast.error('CIM generation is taking longer than expected. Please refresh and try again.')}
    />
  );
}
